import {
  LitElement,
  html,
  css,
  ifDefined,
  classMap,
  broadcastReceiver,
} from '@hh/global.js'

class HHDrawer extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    /*
       side prop is used to set from which side of the screen the drawer opens.
       It can be set to left or right.
       Default is right.
      */
    side: {
      type: String,
      reflect: true,
    },
    /*
        labelledBy and describedBy props to are used to pass and set aria-labelledby and aria-describedby on the dialog element without interfering actual aria functionality
      */
    labelledBy: {
      type: String,
      attribute: 'labelledby',
    },
    describedBy: {
      type: String,
      attribute: 'describedby',
    },
    // Additional properties to support standard aria attributes
    ariaLabelledBy: {
      type: String,
      attribute: 'aria-labelledby',
    },
    ariaDescribedBy: {
      type: String,
      attribute: 'aria-describedby',
    },
    // the aria label is really just for the trunkshow drawer, since that exists in the shadow dom and using the aria-labelledby with something in the liquid won't work
    label: {
      type: String,
    },
    openerSelector: {
      type: String,
      attribute: 'opener-selector',
    },
    mobileOpenerSelector: {
      type: String,
      attribute: 'mobile-opener-selector',
    },
    // If the drawer is supposed to open on hover, pass the selector of the element that triggers the hover *out*. the original opener selector will be used as the hover trigger
    hover: {
      type: String,
    },
    focusDelay: {
      type: Number,
      attribute: 'focus-delay',
    },
    customFocusTrap: {
      type: Boolean,
      attribute: 'custom-focus-trap',
    },
    currentTriggerIndex: { state: true },
    role: {
      type: String,
      reflect: true,
    },
    ariaModal: {
      type: String,
      reflect: true,
      attribute: 'aria-modal',
    },
  }

  constructor() {
    super()

    this.isReducedMotion = matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (!this.side) {
      this.side = 'right'
    }
    this.customFocusTrap = false
    this.role = 'dialog'
    this.ariaModal = 'true'
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('drawer', (...args) => {
      this.openFromDocument(...args)
    })

    window.addEventListener('drawer', this.openFromDocument.bind(this))
    window.addEventListener('keydown', this.onEscKey.bind(this))
    window.addEventListener('drawer:close', this.closeFromEvent.bind(this))

    // By giving the element a hidden class, we can prevent some CLS before the JS is loaded
    this.classList.remove('hidden')

    if (this.hover && window.matchMedia('(min-width: 990px)').matches) {
      let time = 0

      document
        .querySelector(this.openerSelector)
        .addEventListener('mouseover', () => {
          time = setTimeout(() => {
            this.openDrawer()
          }, 500)
        })

      document
        .querySelector(this.openerSelector)
        .addEventListener('mouseleave', () => {
          clearTimeout(time)
        })

      this.querySelector(this.hover).addEventListener(
        'mouseleave',
        this.closeDrawer.bind(this)
      )
    }

    // on mobile, the client services has a "back" button to bring you  back to mobile
    // menu. using `display: none` wasn't working well to remove it from focus flow on desktop
    // so this just removes the element altogether
    if (
      this.id == 'client-services' &&
      window.matchMedia('(min-width: 990px)').matches
    ) {
      document.querySelector('.client-services__close').remove()
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver && this.receiver.disconnect()

    window.removeEventListener('keydown', this.onEscKey)
    window.removeEventListener('drawer:close', this.closeFromEvent)
    window.removeEventListener('drawer', this.closeFromEvent)

    if (this.hover && window.matchMedia('(min-width: 750px)').matches) {
      document
        .querySelector(this.openerSelector)
        .removeEventListener('mouseenter', this.openDrawer)
      this.querySelector(this.hover).removeEventListener(
        'mouseleave',
        this.closeDrawer
      )
    }
  }

  get drawer() {
    return this.shadowRoot.querySelector('.drawer') ?? null
  }

  openDrawer() {
    document.querySelectorAll('hh-drawer').forEach((drawer) => {
      if (drawer !== this && drawer.id != 'trunkshow-drawer') {
        drawer.closeDrawer()
      }
    })
    this.classList.remove('visibility-hidden')

    this.open = true
    document
      .querySelector('.js-announcement-bar-carousel')
      ?.swiper?.autoplay?.stop()

    if (this.id == 'client-services') {
      document
        .querySelector('.header-wrapper')
        .classList.add('client-services--open')
    }

    if (this.id == 'request-appointment') {
      document.querySelector('.post-submit')?.classList.add('hidden')
      document
        .querySelector('.request-appointment__tabs')
        .classList.remove('hidden')
      this.querySelectorAll('input').forEach((input) => {
        if (window.customer && input.id.indexOf('first_name') > -1) {
          input.value = window.customer.first_name
        } else if (window.customer && input.id.indexOf('last_name') > -1) {
          input.value = window.customer.last_name
        } else {
          input.value = ''
        }
      })
    }

    // Adding a delay when setting overflow-hidden class because of rendering issues
    // When opening a drawer from within another drawer, it triggers multiple close function calls
    // The last call in this sequence would be 'close', which removes the overflow-hidden class
    // By delaying the class addition, we ensure that setting overflow-hidden happens after
    // all these calls, making 'open' the final action in the sequence
    setTimeout(() => {
      document.body.classList.add('overflow-hidden')
    }, 500)
    document.documentElement.classList.add('overflow-hidden')
  }

  closeDrawer(e) {
    e?.preventDefault()
    this.open = false

    const params = new URLSearchParams(window.location.search)
    params.delete('modal')
    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState({}, '', newUrl)

    document.documentElement.classList.remove('overflow-hidden')
    document.body.classList.remove('overflow-hidden')

    setTimeout(() => {
      this.classList.add('visibility-hidden')
    }, 500)

    document
      .querySelector('.js-announcement-bar-carousel')
      ?.swiper?.autoplay?.start()
    if (this.id == 'client-services') {
      document
        .querySelector('.header-wrapper')
        .classList.remove('client-services--open')
    }

    document.body.removeAttribute('drawer')

    if (this.id == 'trunkshow-drawer') {
      document.body.removeAttribute('trunkshow')

      window.dispatchEvent(new CustomEvent('trunkshow-closed'))
    }

    if (this.openerSelector) {
      let opener

      if (
        this.mobileOpenerSelector &&
        window.matchMedia('(max-width: 989px)').matches
      ) {
        opener = document.querySelector(this.mobileOpenerSelector)
      } else {
        opener = document.querySelector(this.openerSelector)
      }
      if (this.id === 'trunkshow-drawer' && this.currentTriggerIndex)
        opener = document.querySelector(
          `.js-trunkshow-trigger-${this.currentTriggerIndex}`
        )

      setTimeout(() => {
        opener?.focus({
          preventScroll: true,
          focusVisible: false,
        })
      }, this.focusDelay || 0)
    }

    if (this.id == 'mobile-menu') {
      this.querySelector(
        '[data-sidepanel]:not([hidden]) [data-close-sidepanel]'
      )?.click()
    }

    if (this.id == 'search-drawer') {
      document.querySelector('#Search-In-Modal').value = ''
      document.querySelector('hh-predictive-search').removeAttribute('active')
      document.querySelector('.js-suggested-terms').classList.remove('hidden')
    }
  }

  // Handlers
  openFromDocument(data) {
    if (this.id === data.id || this.id === data.detail?.id) {
      if (this.id === 'trunkshow-drawer') {
        this.currentTriggerIndex = data.triggerIndex
      }
      this.openDrawer()
      window.dispatchEvent(
        new CustomEvent('drawer:opened', {
          detail: {
            id: data.id || data.detail?.id,
          },
        })
      )
    }
  }

  onCloseClick(e) {
    this.closeDrawer(e)
  }

  closeFromEvent(e) {
    this.closeDrawer(e)
  }

  onEscKey(e) {
    // added conditions to prevent closing drawer when user is trying to close a custom select dropdown
    const currentElement = document.activeElement.tagName.toLowerCase()
    if (
      e.key === 'Escape' &&
      this.open &&
      currentElement !== 'hh-custom-select'
    ) {
      this.closeDrawer(e)
    }
  }

  render() {
    // Use aria-prefixed attributes if available, otherwise fall back to non-prefixed versions
    const effectiveLabelledBy = this.ariaLabelledBy || this.labelledBy
    const effectiveDescribedBy = this.ariaDescribedBy || this.describedBy

    return html`
      <div
        class=${classMap({
          overlay: true,
          open: this.open,
          'reduced-motion': this.isReducedMotion,
        })}
        @click=${this.onCloseClick}
        aria-hidden="true"
      ></div>
      <div
        class=${classMap({
          drawer: true,
          open: this.open,
          'reduced-motion': this.isReducedMotion,
        })}
        aria-describedby=${ifDefined(effectiveDescribedBy)}
        ?inert=${!this.open}
      >
        <hh-focus-trap
          active="${ifDefined(this.open && !this.customFocusTrap)}"
        >
          <slot name="close" @click=${this.onCloseClick}></slot>
          <slot></slot>
        </hh-focus-trap>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .overlay {
      position: fixed;
      inset: 0;
      z-index: 109;
      background-color: var(--hh-overlay-color, rgba(0, 0, 0, 0.5));
      opacity: 0;
      pointer-events: none;
      transition: opacity var(--hh-duration-default) ease-in-out;
      min-width: 100%;
      min-height: 100%;
      width: 100vw;
      height: 100vh;
    }

    .overlay.open {
      opacity: 1;
      pointer-events: auto;
    }

    .drawer {
      position: fixed;
      z-index: 110;
      width: 100%;
      max-width: 405px;
      max-height: 100dvh;
      height: 100dvh;
      background-color: var(
        --hh-background-color,
        var(--hh-color-reverse, #fff)
      );
      overflow-y: auto;
      top: 0;
      transition: transform var(--hh-duration-long) var(--hh-cubic-transition);
    }

    :host(#client-services) .drawer {
      height: calc(
        100dvh - var(--hh-announcement-height) - var(--hh-header-height)
      );
      top: calc(var(--hh-announcement-height) + var(--hh-header-height));
    }

    @media screen and (max-width: 989px) {
      :host(#client-services) .drawer {
        height: 100dvh;
        max-width: none;
        top: 0;
      }
    }

    :host([side='left']) .drawer {
      right: auto;
      left: 0;
      max-width: 550px;
      transform: translate3d(-100%, 0, 0);
    }

    :host([side='right']) .drawer {
      right: 0;
      left: auto;
      transform: translate3d(100%, 0, 0);
    }

    :host([side='top']) .drawer {
      max-width: 100vw;
      height: auto;
      transform: translate3d(0, -100%, 0);
    }

    :host([side='bottom']) .drawer {
      max-width: 100vw;
      max-height: var(--hh-max-height, 40rem);
      inset: auto 0 0 0;
      transform: translate3d(0, 100%, 0);
    }

    :host .drawer.open {
      transform: translate3d(0, 0, 0);
    }

    ::slotted([slot='close']) {
      position: absolute;
      cursor: pointer;
      color: var(--hh-close-color, inherit);
      background-color: var(--hh-close-background-color, transparent);
      border: var(--hh-close-border, none);
      border-radius: var(--hh-close-border-radius, 3.5rem);
      font: var(--hh-close-font, inherit);
      top: 0;
      right: 0;
      padding: 20px 15px;
    }

    .drawer.reduced-motion,
    .overlay.reduced-motion {
      transition: none;
    }

    :host([width='full']) .drawer {
      max-width: none;
      width: 100vw;
    }
  `
}

customElements.define('hh-drawer', HHDrawer)
